import { pipe, Redacted, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { WalletAddress, WalletId } from '@root/entities/wallet'
import { DomUtil } from '@root/shared'
import { SecureStorageErrorCode } from '@root/shared/ui/secure-storage-error-code'

import {
  StorageAlreadyUnlockedError,
  WalletAlreadyExistsError,
  WrongPasswordError,
} from '../secure-storage-error'
import {
  type ExportedWallet,
  type Session,
  type WalletKind,
  type WalletOnDevice,
} from '../secure-storage-service'

import { safeInvoke } from './tauri-secure-storage-util'

import type { SecureStorageError } from '../secure-storage-error'
import type {
  ClosestSenderLocationResponse,
  HasWalletsResponseDto,
  WarmSenderConnectionResponse,
} from '../secure-storage-service'
import type { SupportedNetwork } from '@blade/blockchain-types'
import type { HeliusSender } from '@root/entities/network/model/solana'
import type { DelugeId } from '@root/entities/user-short'

export interface ExportedWalletDto {
  item: WalletDto
  mnemonic?: string
  privateKey: string
}

export interface WalletDto {
  walletId: string // UUID, key for keychain
  name: string
  address: string
  network: SupportedNetwork
  order: number
  kind: WalletKind
  createdAt: Date
  updatedAt: Date
}

function walletFromDto(wallet: WalletDto): WalletOnDevice {
  return {
    ...wallet,
    walletId: WalletId(wallet.walletId),
    address: WalletAddress(wallet.address),
    network: Network.fromSupportedNetwork(wallet.network),
  }
}

// Wallets

export async function changePassword(
  delugeId: DelugeId,
  password: Redacted.Redacted,
  newPassword: Redacted.Redacted,
) {
  return (
    await safeInvoke<unknown>('change_password', {
      payload: {
        delugeId,
        password: Redacted.value(password),
        newPassword: Redacted.value(newPassword),
      },
    })
  ).pipe(Result.map(() => undefined))
}

export async function lock() {
  return (await safeInvoke<undefined>('lock', { payload: {} })).pipe(
    Result.map(() => undefined),
  )
}

export async function verifyPassword(
  delugeId: DelugeId,
  password: Redacted.Redacted,
) {
  return (
    await safeInvoke<undefined>('verify_password', {
      payload: {
        delugeId,
        password: Redacted.value(password),
      },
    })
  ).pipe(
    Result.map(() => undefined),
    Result.mapError((err) => {
      if (err.cause === SecureStorageErrorCode.WrongPassword) {
        return new WrongPasswordError()
      }
      return err
    }),
  )
}

export async function initialize(
  delugeId: DelugeId,
  password: Redacted.Redacted,
) {
  return (
    await safeInvoke<undefined>('initialize', {
      payload: {
        delugeId,
        password: Redacted.value(password),
      },
    })
  ).pipe(Result.map(() => undefined))
}

export async function unlock(delugeId: DelugeId, password: Redacted.Redacted) {
  return (
    await safeInvoke<undefined>('unlock', {
      payload: {
        delugeId,
        password: Redacted.value(password),
      },
    })
  ).pipe(
    Result.map(() => undefined),
    Result.mapError((err) => {
      if (err.cause === 'Custom("Decryption failed")') {
        return new WrongPasswordError()
      }
      if (err.cause === SecureStorageErrorCode.StorageIsUnlocked) {
        return new StorageAlreadyUnlockedError()
      }
      return err
    }),
  )
}

export async function getWallets(): Promise<
  Result.Result<ReadonlyArray<WalletOnDevice>, SecureStorageError>
> {
  const dto = await safeInvoke<{ items: Array<WalletDto>; count: number }>(
    'get_wallets',
    {
      payload: {},
    },
  )
  return pipe(
    dto,
    Result.map((dto) => dto.items.map((wallet) => walletFromDto(wallet))),
  )
}

export async function importWallet(
  kind: WalletKind,
  key: Redacted.Redacted<string> | Redacted.Redacted<ReadonlyArray<string>>,
  password: Redacted.Redacted,
  network: Network.Network,
) {
  const revealedKey = Redacted.value(
    key as Redacted.Redacted<string | ReadonlyArray<string>>,
  )
  const resolvedKey = Array.isArray(revealedKey)
    ? revealedKey.join(' ')
    : revealedKey
  const dto = await safeInvoke<{ item: WalletDto }>('import_wallet', {
    payload: {
      kind,
      key: resolvedKey,
      network: Network.toSupportedNetwork(network),
      password: Redacted.value(password),
    },
  })
  return pipe(
    dto,
    Result.map((dto) => walletFromDto(dto.item)),
    Result.mapError((err) => {
      if (typeof err.cause === 'string' && err.cause.startsWith('{')) {
        const jsonResult = DomUtil.parseJsonUnsafe<WalletDto>(
          err.cause as string,
        )
        if (Result.isFailure(jsonResult)) {
          return err
        }
        return new WalletAlreadyExistsError({
          wallet: walletFromDto(jsonResult.success),
        })
      }
      if (err.cause === SecureStorageErrorCode.WrongPassword) {
        return new WrongPasswordError()
      }
      return err
    }),
  )
}

export async function exportWallet(
  walletId: WalletId,
  password: Redacted.Redacted,
): Promise<
  Result.Result<ExportedWallet, SecureStorageError | WrongPasswordError>
> {
  const dto = await safeInvoke<ExportedWalletDto>('export_wallet', {
    payload: { walletId, password: Redacted.value(password) },
  })
  return pipe(
    dto,
    Result.map((dto) => ({
      item: walletFromDto(dto.item),
      privateKey: Redacted.make(dto.privateKey),
      mnemonic:
        dto.mnemonic === undefined ? undefined : Redacted.make(dto.mnemonic),
    })),
    Result.mapError((err) => {
      if (err.cause === SecureStorageErrorCode.WrongPassword) {
        return new WrongPasswordError()
      }
      return err
    }),
  )
}

export async function generateMnemonic() {
  const dto = await safeInvoke<{ mnemonic: string }>('create_mnemonic', {
    payload: {},
  })
  return pipe(
    dto,
    Result.map((dto) => dto.mnemonic),
  )
}

export async function createWallet(
  password: Redacted.Redacted,
  network: Network.Network,
  mnemonic?: string,
) {
  const walletResult = await safeInvoke<{ item: WalletDto }>('create_wallet', {
    payload: {
      password: Redacted.value(password),
      network: Network.toSupportedNetwork(network),
      mnemonic,
    },
  })

  return pipe(
    walletResult,
    Result.map((wallet) => walletFromDto(wallet.item)),
    Result.mapError((err) => {
      if (err.cause === SecureStorageErrorCode.WrongPassword) {
        return new WrongPasswordError()
      }
      return err
    }),
  )
}

export async function removeWallet(walletId: WalletId) {
  const result = await safeInvoke<{}>('remove_wallet', {
    payload: {
      walletId,
    },
  })
  return pipe(
    result,
    Result.map(() => undefined),
  )
}

export async function session(delugeId: DelugeId) {
  return await safeInvoke<Session>('session', { payload: { delugeId } })
}

export async function hasWallets(delugeId: DelugeId) {
  return await safeInvoke<HasWalletsResponseDto>('has_wallets', {
    payload: { delugeId },
  })
}

export async function findClosestSenderLocation() {
  return await safeInvoke<ClosestSenderLocationResponse>(
    'find_closest_sender_location',
    { payload: {} },
  )
}

export async function warmSenderConnection(location: HeliusSender.RpcId) {
  return await safeInvoke<WarmSenderConnectionResponse>(
    'warm_sender_connection',
    {
      payload: { location },
    },
  )
}
